'use client';
// BottomNavigation - Mobile-only bottom navigation bar
import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  alpha,
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Box,
  Portal,
} from '@mui/material';

import { useAuth, useT } from '@/hooks';
import { BottomNavigationSkeleton } from '../../skeletons';

import { ROUTES } from '@/constants';

interface BottomNavigationProps {
  onMenuClick?: () => void;
  onDepositClick?: () => void;
  isLoading?: boolean;
}

const getValueFromPathname = (path: string): string => {
  if (path === ROUTES.HOME) return 'home';
  if (path === ROUTES.BREAKING) return 'breaking';
  if (path === ROUTES.PORTFOLIO) return 'portfolio';
  if (path === ROUTES.SEARCH) return 'search';
  return '';
};

export const BottomNavigation = ({
  onMenuClick,
  onDepositClick,
  isLoading,
}: BottomNavigationProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const { t } = useT();

  const activeTab = useMemo(() => getValueFromPathname(pathname), [pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    switch (newValue) {
      case 'home':
        router.push(ROUTES.HOME);
        break;
      case 'breaking':
        router.push(ROUTES.BREAKING);
        break;
      case 'search':
        router.push(ROUTES.SEARCH);
        break;
      case 'deposit':
        onDepositClick?.();
        break;
      case 'portfolio':
        router.push(ROUTES.PORTFOLIO);
        break;
      case 'more':
        onMenuClick?.();
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return <BottomNavigationSkeleton />;
  }

  return (
    <>
      {/* Deposit's raised circle, portal-rendered straight to <body>. Every
          ancestor we control (this component and MUI's own) has overflow set
          to visible, yet the circle still clipped where it poked above the
          bar — pointing at a containing-block/overflow ancestor somewhere
          higher in the tree we don't control. Rendering fully outside this
          component's DOM subtree sidesteps that regardless of the cause.
          pointerEvents: 'none' so clicks pass through to the real (invisible)
          tab button underneath, which stays the actual click target. */}
      {isAuthenticated && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              display: { xs: 'flex', md: 'none' },
              bottom: '28px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1150,
              pointerEvents: 'none',
              width: 45,
              height: 45,
              borderRadius: '50px',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              boxShadow: (theme) =>
                `0 0 0 5px ${theme.palette.background.paper}, 0 4px 16px ${alpha(
                  theme.palette.primary.main,
                  0.6,
                )}`,
            }}
          >
            <AddIcon sx={{ fontSize: '1.75rem', color: 'primary.contrastText' }} />
          </Box>
        </Portal>
      )}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          display: { xs: 'block', md: 'none' },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? `0 -2px 8px ${alpha(theme.palette.common.black, 0.5)}`
              : `0 -2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >
        <MuiBottomNavigation
          value={activeTab}
          onChange={handleChange}
          showLabels
          sx={{
            height: 64,
            overflow: 'visible',
            bgcolor: 'background.paper',
            '& .MuiBottomNavigationAction-root': {
              color: 'text.secondary',
              minWidth: 0,
              padding: '6px 12px',
              '&.Mui-selected': {
                color: 'primary.main',
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.6875rem',
                fontWeight: 500,
                mt: 0.5,
                '&.Mui-selected': {
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                },
              },
            },
          }}
        >
          <BottomNavigationAction
            label={t('nav.home')}
            value="home"
            icon={<HomeIcon sx={{ fontSize: '1.5rem' }} />}
          />
          <BottomNavigationAction
            label={t('nav.breaking')}
            value="breaking"
            icon={<BoltIcon sx={{ fontSize: '1.5rem' }} />}
          />
          {isAuthenticated && (
            <BottomNavigationAction
              label={t('nav.deposit')}
              value="deposit"
              // Same-size-as-the-others invisible spacer: keeps this action's real
              // click target and label position identical to every other tab. The
              // visible circle is a separate Portal element (see below) — a real
              // ancestor somewhere in the tree clips anything that visually pokes
              // above this row despite overflow: visible at every level we control,
              // so the circle is rendered straight to <body> instead, immune to it.
              icon={<Box sx={{ width: 24, height: 24, flexShrink: 0 }} />}
              // The parent MuiBottomNavigation's `& .MuiBottomNavigationAction-root
              // .MuiBottomNavigationAction-label` rule (3 classes) outranks this
              // action's own `& .MuiBottomNavigationAction-label` (2 classes) by
              // CSS specificity regardless of source order — every previous mt
              // tweak here was silently losing to it. !important forces this one
              // instance to actually win.
              sx={{ '& .MuiBottomNavigationAction-label': { mt: '15px !important' } }}
            />
          )}
          <BottomNavigationAction
            label={t('nav.search')}
            value="search"
            icon={<SearchIcon sx={{ fontSize: '1.5rem' }} />}
          />
          {isAuthenticated && (
            <BottomNavigationAction
              label={t('nav.portfolio')}
              value="portfolio"
              icon={<AccountBalanceWalletIcon sx={{ fontSize: '1.5rem' }} />}
            />
          )}
          {!isAuthenticated && (
            <BottomNavigationAction
              label={t('nav.more')}
              value="more"
              icon={<MenuIcon sx={{ fontSize: '1.5rem' }} />}
            />
          )}
        </MuiBottomNavigation>
      </Box>
    </>
  );
};

export default BottomNavigation;
